<template>
  <table
    @click="handleMonthTableClick"
    @mousemove="handleMouseMove"
    class="el-quarter-table"
  >
    <tbody>
      <tr v-for="(row, key) in rows" :key="key">
        <td :class="getCellStyle(cell)" v-for="(cell, key) in row" :key="key">
          <div>
            <a class="cell">{{ months[cell.text] }}</a>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script type="text/babel">
import Locale from "element-ui/src/mixins/locale";
import { isDate } from "element-ui/src/utils/date-util";
import { hasClass } from "element-ui/src/utils/dom";
import {
  arrayFindIndex,
  coerceTruthyValueToArray,
  arrayFind,
} from "element-ui/src/utils/util";
import dayjs from "dayjs";

var quarterOfYear = require("dayjs/plugin/quarterOfYear");
dayjs.extend(quarterOfYear);
var isSameOrAfter = require("dayjs/plugin/isSameOrAfter");
dayjs.extend(isSameOrAfter);

const datesInQuarter = (year, quarter) => {
  const dateInQuarter = [];
  let day = dayjs(year.toString()).quarter(quarter).startOf("quarter");
  const endDay = dayjs(day).endOf("quarter");
  let isNotEnd = true;
  while (isNotEnd) {
    dateInQuarter.push(day.toDate());
    if (dayjs(day).isSameOrAfter(endDay)) {
      isNotEnd = false;
    }
    day = day.add(1, "day");
  }
  return dateInQuarter;
};

const getQuarterTimestamp = function (time) {
  if (
    typeof time === "number" ||
    typeof time === "string" ||
    time instanceof Date
  ) {
    return dayjs(time).startOf("quarter").toDate().getTime();
  } else {
    return NaN;
  }
};
export default {
  props: {
    disabledDate: {},
    value: {},
    selectionMode: {
      default: "quarter",
    },
    minDate: {},

    maxDate: {},
    defaultValue: {
      validator(val) {
        // null or valid Date Object
        return (
          val === null ||
          isDate(val) ||
          (Array.isArray(val) && val.every(isDate))
        );
      },
    },
    date: {},
    rangeState: {
      default() {
        return {
          endDate: null,
          selecting: false,
        };
      },
    },
  },

  mixins: [Locale],

  watch: {
    "rangeState.endDate"(newVal) {
      this.markRange(this.minDate, newVal);
    },

    minDate(newVal, oldVal) {
      if (getQuarterTimestamp(newVal) !== getQuarterTimestamp(oldVal)) {
        this.markRange(this.minDate, this.maxDate);
      }
    },

    maxDate(newVal, oldVal) {
      if (getQuarterTimestamp(newVal) !== getQuarterTimestamp(oldVal)) {
        this.markRange(this.minDate, this.maxDate);
      }
    },
  },

  data() {
    return {
      months: ["第1季度", "第2季度", "第3季度", "第4季度"],
      tableRows: [[], []],
      lastRow: null,
      lastColumn: null,
    };
  },

  methods: {
    cellMatchesDate(cell, date) {
      const value = new Date(date);
      return (
        this.date.getFullYear() === value.getFullYear() &&
        Number(cell.text) + 1 === dayjs(value).quarter()
      );
    },
    getCellStyle(cell) {
      const style = {};
      const year = this.date.getFullYear();
      const today = new Date();
      const quarter = cell.text + 1;
      const defaultValue = this.defaultValue
        ? Array.isArray(this.defaultValue)
          ? this.defaultValue
          : [this.defaultValue]
        : [];
      style.disabled =
        typeof this.disabledDate === "function"
          ? datesInQuarter(year, quarter).every(this.disabledDate)
          : false;
      style.current =
        arrayFindIndex(
          coerceTruthyValueToArray(this.value),
          (date) =>
            date.getFullYear() === year && dayjs(date).quarter() === quarter
        ) >= 0;
      style.today =
        today.getFullYear() === year && dayjs(today).quarter() === quarter;
      style.default = defaultValue.some((date) =>
        this.cellMatchesDate(cell, date)
      );

      if (cell.inRange) {
        style["in-range"] = true;

        if (cell.start) {
          style["start-date"] = true;
        }

        if (cell.end) {
          style["end-date"] = true;
        }
      }
      return style;
    },
    getQuarterDateOfCell(quarter) {
      const year = this.date.getFullYear();
      return dayjs(year.toString())
        .quarter(quarter)
        .startOf("quarter")
        .toDate();
    },
    markRange(minDate, maxDate) {
      minDate = getQuarterTimestamp(minDate);
      maxDate = getQuarterTimestamp(maxDate) || minDate;
      [minDate, maxDate] = [
        Math.min(minDate, maxDate),
        Math.max(minDate, maxDate),
      ];
      const rows = this.rows;
      for (let i = 0, k = rows.length; i < k; i++) {
        const row = rows[i];
        for (let j = 0, l = row.length; j < l; j++) {
          const cell = row[j];
          const index = i * 2 + j;
          const time = dayjs(this.date.getFullYear().toString())
            .quarter(index + 1)
            .startOf("quarter")
            .toDate()
            .getTime();

          cell.inRange = minDate && time >= minDate && time <= maxDate;
          cell.start = minDate && time === minDate;
          cell.end = maxDate && time === maxDate;
        }
      }
    },
    handleMouseMove(event) {
      if (!this.rangeState.selecting) return;

      let target = event.target;
      if (target.tagName === "A") {
        target = target.parentNode.parentNode;
      }
      if (target.tagName === "DIV") {
        target = target.parentNode;
      }
      if (target.tagName !== "TD") return;

      const row = target.parentNode.rowIndex;
      const column = target.cellIndex;
      // can not select disabled date
      if (this.rows[row][column].disabled) return;

      // only update rangeState when mouse moves to a new cell
      // this avoids frequent Date object creation and improves performance
      if (row !== this.lastRow || column !== this.lastColumn) {
        this.lastRow = row;
        this.lastColumn = column;
        this.$emit("changerange", {
          minDate: this.minDate,
          maxDate: this.maxDate,
          rangeState: {
            selecting: true,
            endDate: this.getQuarterDateOfCell(row * 2 + column + 1),
          },
        });
      }
    },
    handleMonthTableClick(event) {
      let target = event.target;
      if (target.tagName === "A") {
        target = target.parentNode.parentNode;
      }
      if (target.tagName === "DIV") {
        target = target.parentNode;
      }
      if (target.tagName !== "TD") return;
      if (hasClass(target, "disabled")) return;
      const column = target.cellIndex;
      const row = target.parentNode.rowIndex;
      const quarter = row * 2 + column;
      const newDate = this.getQuarterDateOfCell(quarter + 1);
      if (this.selectionMode === "range") {
        if (!this.rangeState.selecting) {
          this.$emit("pick", { minDate: newDate, maxDate: null });
          this.rangeState.selecting = true;
        } else {
          if (newDate >= this.minDate) {
            this.$emit("pick", {
              minDate: this.minDate,
              maxDate: newDate,
            });
          } else {
            this.$emit("pick", {
              minDate: newDate,
              maxDate: this.minDate,
            });
          }
          this.rangeState.selecting = false;
        }
      } else {
        this.$emit("pick", quarter);
      }
    },
  },

  computed: {
    rows() {
      // TODO: refactory rows / getCellClasses
      const rows = this.tableRows;
      const disabledDate = this.disabledDate;
      const selectedDate = [];
      const now = getQuarterTimestamp(new Date());

      for (let i = 0; i < 2; i++) {
        const row = rows[i];
        for (let j = 0; j < 2; j++) {
          let cell = row[j];
          if (!cell) {
            cell = {
              row: i,
              column: j,
              type: "normal",
              inRange: false,
              start: false,
              end: false,
            };
          }

          cell.type = "normal";
          /** 第几季度, 需要加一，不加一是索引 */
          const index = i * 2 + j;

          const time = dayjs(this.date.getFullYear().toString())
            .quarter(index + 1)
            .startOf("quarter")
            .toDate()
            .getTime();

          cell.inRange =
            time >= getQuarterTimestamp(this.minDate) &&
            time <= getQuarterTimestamp(this.maxDate);

          cell.start =
            this.minDate && time === getQuarterTimestamp(this.minDate);
          cell.end = this.maxDate && time === getQuarterTimestamp(this.maxDate);
          const isToday = time === now;

          if (isToday) {
            cell.type = "today";
          }
          cell.text = index;
          let cellDate = new Date(time);
          cell.disabled =
            typeof disabledDate === "function" && disabledDate(cellDate);
          cell.selected = arrayFind(
            selectedDate,
            (date) => date.getTime() === cellDate.getTime()
          );

          this.$set(row, j, cell);
        }
      }
      return rows;
    },
  },
};
</script>

<style lang="scss">
// @import '../common/var';
@import "@/assets/style/var.scss";
@mixin b($block) {
  $B: "el-" + $block !global;

  .#{$B} {
    @content;
  }
}
// $--color-primary: #009688 !default;
// $--color-white: #FFFFFF !default;
// $--background-color-base: #F5F7FA !default;
// $--border-color-base: #DCDFE6 !default;
// $--color-text-placeholder: #C0C4CC !default;
// $--color-text-regular: #606266 !default;
// $--datepicker-font-color: $--color-text-regular !default;
// $--datepicker-hover-font-color: $--color-primary !default;
// $--border-color-extra-light: #F2F6FC !default;
// $--datepicker-inrange-background-color: $--border-color-extra-light !default;
// $--datepicker-inrange-hover-background-color: $--border-color-extra-light !default;
// $--datepicker-active-color: $--color-primary !default;
@include b(quarter-table) {
  font-size: 12px;
  margin: -1px;
  border-collapse: collapse;

  td {
    text-align: center;
    padding: 8px 0px;
    cursor: pointer;
    & div {
      height: 48px;
      padding: 6px 0;
      box-sizing: border-box;
    }
    &.today {
      .cell {
        color: $--color-primary;
        font-weight: bold;
      }
      &.start-date .cell,
      &.end-date .cell {
        color: $--color-white;
      }
    }

    &.disabled .cell {
      background-color: $--background-color-base;
      cursor: not-allowed;
      color: $--color-text-placeholder;

      &:hover {
        color: $--color-text-placeholder;
      }
    }

    .cell {
      width: 130px;
      height: 36px;
      display: block;
      line-height: 36px;
      color: $--datepicker-font-color;
      margin: 0 auto;
      border-radius: 18px;
      &:hover {
        color: $--datepicker-hover-font-color;
      }
    }

    &.in-range div {
      background-color: $--datepicker-inrange-background-color;
      &:hover {
        background-color: $--datepicker-inrange-hover-background-color;
      }
    }
    &.start-date div,
    &.end-date div {
      color: $--color-white;
    }

    &.start-date .cell,
    &.end-date .cell {
      color: $--color-white;
      background-color: $--datepicker-active-color;
    }

    &.start-date div {
      border-top-left-radius: 24px;
      border-bottom-left-radius: 24px;
    }

    &.end-date div {
      border-top-right-radius: 24px;
      border-bottom-right-radius: 24px;
    }

    &.current:not(.disabled) .cell {
      color: $--datepicker-active-color;
    }
  }
}
</style>
